import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    Grid,
    useMediaQuery,
    useTheme,
    Button // <-- Add this import
} from '@mui/material';
import { convertToRS } from 'core/js/FormUtils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { getStatementAPI } from 'api/CoreMlmmgmtWebServices';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MlmcreditListByPaymentId from './MlmcreditListByPaymentId'; // Adjust path if needed

export default function CreditStatement({ setBalance, setTranferInProgressBalance }) {
    const [statementData, setStatementData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState('');
    const theme = useTheme();
    const isTablet = useMediaQuery('(min-width:600px) and (max-width:1199px)');
    const isLaptop = useMediaQuery('(min-width:1200px)');
    const cardsPerRow = isLaptop ? 4 : isTablet ? 3 : 1;
    const {refreshCreditSummary} = useSelector((state)=>state.ecomslice.mlm);

    useEffect(() => {
        //loadCreditStatement();
    }, []);
    useEffect(()=>{
        loadCreditStatement();  
    },[refreshCreditSummary])

    const loadCreditStatement = () => {
        setIsLoading(true);
        getStatementAPI({},
            (response) => {
                setBalance(response.balance);
                setTranferInProgressBalance(response.tranferInProgressBalance);
                // Sort statements by date (latest to oldest)
                const sortedStatements = (response.statements || []).sort(
                    (a, b) => new Date(b.date) - new Date(a.date)
                );
                setStatementData(sortedStatements);
                setIsLoading(false);
            },
            () => {
                setIsLoading(false);
            }
        )
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatAmount = (amount) => {
        return `₹${(amount / 100).toFixed(2)}`;
    };

    // Loading Component
    const LoadingView = () => (
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '300px',
            flexDirection: 'column',
            gap: 2
        }}>
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary">
                Loading credit statement...
            </Typography>
        </Box>
    );

    // Mobile Card View (used for all screens now)
    const MobileStatementCard = ({ row }) => (
        <Card sx={{ mb: 2, boxShadow: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.credit > 0 && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                backgroundColor: 'success.50',
                                p: 1,
                                borderRadius: 1
                            }}>
                                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.85rem' }}>
                                    {formatAmount(row.credit * 100)}
                                </Typography>
                            </Box>
                        )}
                        {row.debit > 0 && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                backgroundColor: 'error.50',
                                p: 1,
                                borderRadius: 1
                            }}>
                                <TrendingDownIcon sx={{ color: 'error.main', fontSize: 18 }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main', fontSize: '0.85rem' }}>
                                    {formatAmount(row.debit * 100)}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{
                            width: '2px',
                            height: '30px',
                            backgroundColor: 'divider',
                            mx: 0.5
                        }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.25rem' }}>
                            {formatAmount(row.balance * 100)}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, backgroundColor: 'grey.100', p: 1, borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.description}
                    </Typography>
                    {row.credit > 0 && (
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                            onClick={() => handleViewDetails(row.paymentId)}
                        >
                            Details
                        </Button>
                    )}
                </Box>
                <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', display: 'block' }}
                    >
                        {row.credit > 0
                            ? `Credited on ${formatDate(row.date)}`
                            : row.debit > 0
                                ? `Debited on ${formatDate(row.date)}`
                                : formatDate(row.date)
                        }
                        &nbsp;via <b>{row.creditVia}</b>
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );

    const handleViewDetails = (paymentid) => {
        console.log('handleViewDetails:', paymentid);
        setSelectedPaymentId(paymentid);
        setViewDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setViewDetailsOpen(false);
        setSelectedPaymentId('');
    };

    return (
        <Box>
            {isLoading ? (
                <LoadingView />
            ) : (
                <>
                    {statementData && statementData.length > 0 ? (
                        <Box
                            sx={{
                                //maxHeight: isLaptop ? '85vh' : isTablet ? '75vh' : '60vh',
                                overflow: 'auto',
                                pr: 1
                            }}
                        >
                            <Grid container spacing={2}>
                                {statementData.map((row) => (
                                    <Grid item xs={12} sm={12} md={12 / cardsPerRow} lg={12 / cardsPerRow} key={row.id}>
                                        <MobileStatementCard row={row} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ) : (
                        <Card sx={{ textAlign: 'center', py: 4 }}>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary">
                                    No statement records found
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                    <Dialog
                        open={viewDetailsOpen}
                        onClose={handleCloseDetails}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogContent sx={{ p: 0 }}>
                            {selectedPaymentId && (
                                <MlmcreditListByPaymentId paymentId={selectedPaymentId} onClose={handleCloseDetails} />
                            )}
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </Box>
    );
}